import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Handshake, Sparkles, Tag, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { layoutForPath } from '../utils/layout';
import DataImportDialog from '../components/import/DataImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: '', category: '', contact: '' };

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isGold, setIsGold] = useState(false);

  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');

  const [catDialog, setCatDialog] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');

  useEffect(() => {
    load();
    loadCategories();
  }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/vendors`);
      setVendors(res.data);
    } catch {
      toast.error('Failed to load vendors');
    }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/vendor-categories`);
      setCategories(res.data.categories || []);
      setIsGold(!!res.data.is_gold_tier);
    } catch {
      // silent
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialog(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({ name: v.name, category: v.category || '', contact: v.contact || '' });
    setDialog(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      category: form.category || null,
      contact: form.contact || null,
    };
    if (!payload.name) { toast.error('Name required'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/vendors/${editing.id}`, payload);
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/vendors`, payload);
        toast.success('Vendor added');
      }
      setDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try {
      await axios.delete(`${API}/vendors/${v.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    const label = newCatLabel.trim();
    if (!label) return;
    try {
      await axios.post(`${API}/vendor-categories`, { label });
      toast.success(`Added "${label}"`);
      setNewCatLabel('');
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.label}"? Vendors currently assigned to it will be un-categorised.`)) return;
    try {
      await axios.delete(`${API}/vendor-categories/${cat.value}`);
      toast.success('Category deleted');
      loadCategories();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const labelFor = (value) => {
    if (!value) return null;
    const c = categories.find((x) => x.value === value);
    return c?.label || value;
  };

  const filtered = vendors.filter((v) =>
    !search.trim() ||
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (labelFor(v.category) || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.contact || '').toLowerCase().includes(search.toLowerCase())
  );

  const customCategories = categories.filter((c) => !c.system);
  const { user } = useAuth();
  const isMgmt = user?.role === 'management' || (user?.effective_permissions || []).includes('vendors.edit');
  const canAddVendor = ['management', 'foreman', 'field_coordinator', 'field_management'].includes(user?.role)
    || (user?.effective_permissions || []).includes('vendors.edit');
  const location = useLocation();
  const Layout = layoutForPath(location.pathname);

  return (
    <Layout title="Vendors">
      <div className="space-y-6" data-testid="vendors-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2">
              <Handshake size={22} /> Vendors
            </h3>
            <p className="text-slate-600 text-sm">Third-party service providers and parts suppliers. Auto-populated when logging vendor maintenance on any equipment.</p>
          </div>
          <div className="flex gap-2">
            {isMgmt && (
              <Button variant="outline" onClick={() => setCatDialog(true)} data-testid="manage-categories-button">
                <Tag size={16} className="mr-1" /> Manage Categories
              </Button>
            )}
            {user?.role === 'management' && (
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-vendors-button">
                <Upload size={16} className="mr-1" /> Import CSV
              </Button>
            )}
            {canAddVendor && (
              <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="add-vendor-button">
                <Plus size={18} className="mr-1" /> Add Vendor
              </Button>
            )}
          </div>
        </div>

        <DataImportDialog kind="vendors" open={importOpen} onOpenChange={setImportOpen} onImported={load} />

        <div className="flex items-center gap-3">
          <Input
            placeholder="Search vendors by name, category, or contact"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
            data-testid="vendor-search"
          />
          <span className="text-xs text-slate-500">{filtered.length} of {vendors.length}</span>
        </div>

        {/* Add/Edit Vendor Dialog */}
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
              <DialogDescription>Vendors are traceable across all equipment maintenance logs.</DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3" data-testid="vendor-form">
              <div className="space-y-1">
                <Label className="text-xs">Vendor Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="vendor-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={form.category || '__none'} onValueChange={(v) => setForm({ ...form, category: v === '__none' ? '' : v })}>
                  <SelectTrigger data-testid="vendor-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— none —</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Contact (phone / email / website)</Label>
                <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="e.g. 555-1234 or hello@vendor.com" data-testid="vendor-contact-input" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-vendor">
                {editing ? 'Update Vendor' : 'Add Vendor'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Manage Categories Dialog */}
        <Dialog open={catDialog} onOpenChange={setCatDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Vendor Categories</DialogTitle>
              <DialogDescription>
                Service and Parts are always available. {isGold ? 'Add or remove your own custom categories below.' : 'Add your own custom categories on a Gold tier plan.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4" data-testid="manage-categories-body">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Default</div>
                <div className="flex flex-wrap gap-2">
                  {categories.filter((c) => c.system).map((c) => (
                    <span key={c.value} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-sm font-medium" data-testid={`system-cat-${c.value}`}>
                      {c.label}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-2">
                  Custom
                  {!isGold && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase" data-testid="gold-only-badge">
                      <Sparkles size={10} /> Gold tier
                    </span>
                  )}
                </div>
                {customCategories.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No custom categories yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {customCategories.map((c) => (
                      <span key={c.value} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium" data-testid={`custom-cat-${c.value}`}>
                        {c.label}
                        {isGold && (
                          <button type="button" onClick={() => removeCategory(c)} className="text-blue-700 hover:text-red-600" data-testid={`delete-cat-${c.value}`} aria-label={`Delete ${c.label}`}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {isGold ? (
                <form onSubmit={addCategory} className="flex gap-2 pt-2 border-t border-slate-200" data-testid="add-category-form">
                  <Input
                    placeholder="e.g. Freight Carrier"
                    value={newCatLabel}
                    onChange={(e) => setNewCatLabel(e.target.value)}
                    maxLength={40}
                    data-testid="new-category-input"
                  />
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-category">
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </form>
              ) : (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="gold-upsell">
                  <div className="flex items-center gap-2 font-semibold mb-1"><Sparkles size={14} /> Gold tier feature</div>
                  Upgrade to add your own categories (e.g. Freight Carrier, Machining Sub, Rentals).
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm">
          <table className="w-full" data-testid="vendors-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Category</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filtered.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50" data-testid={`vendor-row-${v.id}`}>
                  <td className="px-6 py-3 text-sm font-medium text-slate-900">{v.name}</td>
                  <td className="px-6 py-3 text-sm text-slate-700">
                    {v.category
                      ? (labelFor(v.category) || v.category)
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-700">{v.contact || <span className="text-slate-400">—</span>}</td>
                  <td className="px-6 py-3">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(v)} className="text-red-600 hover:bg-red-50" data-testid={`delete-vendor-${v.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              {vendors.length === 0
                ? 'No vendors yet. Add one manually or log vendor maintenance on any equipment to auto-create.'
                : 'No vendors match your search.'}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
